/**
 * P-18 审计日志（docs/03-UIUX.md §5）。
 *
 * 只读流水，分页展示。行点击展开查看完整 payload（detail / 哈希链 / IP / UA）。
 * 管理端可按「操作」下拉筛选；owner 可单条 / 批量删除（删除会打断哈希链，属允许的清理权衡）。
 */
import { useCallback, useState } from 'react';
import { ErrorBlock, EmptyFirstRun, EmptyFiltered } from '../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../components/Pager';
import { describeError } from '../api/client';
import { deleteAuditEvent, deleteAuditEvents, listAuditEvents, type AuditEvent } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { formatDateTime } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { useAuth } from '../auth/AuthContext';

const ACTION_LABEL: Record<string, string> = {
  'auth.login': '登录',
  'auth.logout': '登出',
  'auth.login_lock': '登录锁定',
  'auth.password_change': '修改密码',
  'product.create': '创建产品',
  'product.update': '更新产品',
  'product.delete': '删除产品',
  'product_version.create': '创建版本',
  'product_version.update': '更新版本',
  'product_version.delete': '删除版本',
  'plan.create': '创建套餐',
  'plan.update': '更新套餐',
  'plan.delete': '删除套餐',
  'entitlement_key.create': '创建功能开关',
  'entitlement_key.update': '更新功能开关',
  'entitlement_key.delete': '删除功能开关',
  'customer.create': '创建客户',
  'customer.update': '更新客户',
  'customer.delete': '删除客户',
  'order.create': '创建订单',
  'order.update': '更新订单',
  'code_batch.create': '创建批次',
  'code_batch.export': '导出批次',
  'license_code.redeem': '兑换授权码',
  'license_code.void': '作废授权码',
  'license_code.bulk_void': '批量作废授权码',
  'license.create': '手动创建授权',
  'license.update': '更新授权',
  'license.revoke': '撤销授权',
  'license.suspend': '暂停授权',
  'license.resume': '恢复授权',
  'license.renew': '续期授权',
  'activation.activate': '激活设备',
  'activation.deactivate': '解绑设备',
  'activation.reclaimed': '自动回收闲置设备',
  'activation.heartbeat': '心跳',
  'signing_key.rotate': '轮换密钥',
  'signing_key.compromise': '应急吊销密钥',
  'signing_key.retire': '下线密钥',
  'api_key.create': '创建 API Key',
  'api_key.revoke': '吊销 API Key',
  'admin_user.create': '创建管理员',
  'admin_user.update': '更新管理员',
  'admin_user.disable': '停用管理员',
  'admin_user.reset_password': '重置密码',
};

/** 下拉选项（全部操作 + 「全部操作」）。Object 字面量插入序即展示序。 */
const ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '全部操作' },
  ...Object.entries(ACTION_LABEL).map(([value, label]) => ({ value, label })),
];

function actionLabel(action: string): string {
  return ACTION_LABEL[action] ?? action;
}

export function AuditPage() {
  const { currentUser } = useAuth();
  const isOwner = currentUser?.role === 'owner';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [filterAction, setFilterAction] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<AuditEvent | null>(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);

  const hasActiveFilters = filterAction !== '';

  const events = useAsync(
    useCallback(
      () => listAuditEvents({ page, limit, action: filterAction || undefined }),
      [page, limit, filterAction],
    ),
    [page, limit, filterAction],
  );

  const eventList: readonly AuditEvent[] = events.state.kind === 'ready' ? events.state.data.items : [];
  const total = events.state.kind === 'ready' ? events.state.data.total : 0;
  const hasMore = events.state.kind === 'ready' ? events.state.data.hasMore : false;

  const allSelected = eventList.length > 0 && eventList.every((e) => selectedIds.has(e.id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const e of eventList) next.delete(e.id);
      } else {
        for (const e of eventList) next.add(e.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const toggleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">审计日志</h1>
          <p className="page-head__sub">
            只读操作流水，按时间倒序排列。每条记录由哈希链保护完整性，行点击可查看完整 payload。
          </p>
        </div>
      </div>

      <div className="filter-bar">
        <div className="filter-bar__group">
          <label className="filter-bar__label">操作</label>
          <select
            className="filter-bar__select"
            value={filterAction}
            onChange={(e) => { setFilterAction(e.target.value); setPage(1); }}
          >
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {events.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(events.state.error, '加载审计日志')} onRetry={events.reload} />
        </div>
      ) : events.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table">
            <AuditSkeleton rows={10} />
          </table>
        </div>
      ) : eventList.length === 0 && !hasActiveFilters ? (
        <div className="table-frame">
          <EmptyFirstRun icon="scroll-text" description="暂无审计日志。操作发生后会自动记录。" />
        </div>
      ) : eventList.length === 0 && hasActiveFilters ? (
        <div className="table-frame">
          <EmptyFiltered
            description="没有匹配的审计记录，试试调整筛选条件。"
            onClearFilters={() => { setFilterAction(''); setPage(1); }}
          />
        </div>
      ) : (
        <>
          <div className="table-frame">
            <table className="table">
              <thead>
                <tr>
                  {isOwner && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="全选本页"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={eventList.length === 0}
                      />
                    </th>
                  )}
                  <th style={{ width: 36 }} />
                  <th>时间</th>
                  <th>操作</th>
                  <th>操作者</th>
                  <th>目标</th>
                  <th>IP</th>
                  {isOwner && <th className="table__actions-col">操作</th>}
                </tr>
              </thead>
              <tbody>
                {eventList.map((event) => (
                  <AuditRow
                    key={event.id}
                    event={event}
                    expanded={expandedId === event.id}
                    onToggle={() => toggleExpand(event.id)}
                    isOwner={isOwner}
                    selected={selectedIds.has(event.id)}
                    onSelect={() => toggleSelect(event.id)}
                    onDelete={() => setDeleteTarget(event)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {isOwner && (
            <div className="filter-bar" style={{ marginTop: 12 }}>
              <div className="filter-bar__group">
                <span className="text-muted" style={{ fontSize: 13 }}>
                  {selectedIds.size > 0 ? `已选 ${selectedIds.size} 条` : '勾选记录进行批量删除'}
                </span>
                {selectedIds.size > 0 && (
                  <>
                    <button
                      type="button"
                      className="btn btn--quiet btn--danger-text"
                      onClick={() => setShowBulkDelete(true)}
                    >
                      <Icon name="trash" size={ICON_SIZE.inline} />
                      批量删除（{selectedIds.size}）
                    </button>
                    <button type="button" className="btn btn--quiet" onClick={clearSelection}>
                      清空选择
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          <Pager
            page={page}
            limit={limit}
            total={total}
            hasMore={hasMore}
            onPageChange={setPage}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
          />
        </>
      )}

      {deleteTarget !== null && (
        <DeleteDialog
          event={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            events.reload();
          }}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          count={selectedIds.size}
          preview={eventList.filter((e) => selectedIds.has(e.id))}
          onClose={() => setShowBulkDelete(false)}
          onDeleted={async () => {
            await deleteAuditEvents([...selectedIds]);
            setShowBulkDelete(false);
            clearSelection();
            events.reload();
          }}
        />
      )}
    </>
  );
}

function AuditRow({
  event,
  expanded,
  onToggle,
  isOwner,
  selected,
  onSelect,
  onDelete,
}: {
  event: AuditEvent;
  expanded: boolean;
  onToggle: () => void;
  isOwner: boolean;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const detailText = event.detail === null || event.detail === undefined
    ? null
    : JSON.stringify(event.detail, null, 2);
  const failed = event.result === 'failure';

  return (
    <>
      <tr className={`${expanded ? 'table__row-expanded' : ''} ${failed ? 'audit-row--failure' : ''}`}>
        {isOwner && (
          <td style={{ width: 36 }}>
            <input
              type="checkbox"
              aria-label="选择该记录"
              checked={selected}
              onChange={onSelect}
            />
          </td>
        )}
        <td style={{ width: 36 }}>
          <span className="row-actions">
            {failed && (
              <Icon name="octagon-x" size={ICON_SIZE.inline} color="var(--st-danger)" aria-label="失败事件" />
            )}
            <button
              type="button"
              className="btn btn--quiet btn--icon"
              aria-label={expanded ? '收起详情' : '展开详情'}
              title={expanded ? '收起' : '展开'}
              onClick={onToggle}
            >
              <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={ICON_SIZE.inline} />
            </button>
          </span>
        </td>
        <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(event.created_at)}</td>
        <td>{actionLabel(event.action)}</td>
        <td className="text-muted">{event.actor_type === 'admin' ? '管理员' : event.actor_type}</td>
        <td className="text-muted">{event.target_type} / {event.target_id?.slice(0, 8) ?? '-'}</td>
        <td><code style={{ fontSize: 11 }}>{event.ip ?? '-'}</code></td>
        {isOwner && (
          <td className="table__actions-col">
            <button
              type="button"
              className="btn btn--quiet btn--danger-text"
              onClick={onDelete}
              title="删除该审计记录"
            >
              <Icon name="trash" size={ICON_SIZE.inline} />
              删除
            </button>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="audit-detail-row">
          <td />
          <td colSpan={isOwner ? 6 : 5}>
            <div className="audit-detail">
              {detailText ? (
                <>
                  <div className="audit-detail__label">detail payload</div>
                  <pre className="audit-detail__payload">{detailText}</pre>
                </>
              ) : (
                <p className="text-muted" style={{ fontSize: 12 }}>detail：无</p>
              )}
              <div className="audit-detail__meta">
                <span>哈希链：<code>{event.entry_hash.slice(0, 16)}…</code>（前驱 <code>{event.prev_hash.slice(0, 16)}…</code>）</span>
                <span>UA：<code>{event.user_agent ?? '-'}</code></span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DeleteDialog({
  event,
  onClose,
  onDeleted,
}: {
  event: AuditEvent;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteAuditEvent(event.id);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : '删除失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="删除审计记录">
        <div className="panel__head">
          <h2 className="panel__title">删除审计记录</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将删除这条审计记录（<strong>{actionLabel(event.action)}</strong>，{formatDateTime(event.created_at)}）。
          </p>
          <p className="text-muted" style={{ fontSize: 12 }}>
            注意：审计记录由哈希链串联，删除会打断链条——链校验将标记断点。这是允许的清理权衡，请确认该记录无需留存。
          </p>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting} onClick={handle}>
              {submitting ? '删除中…' : '确认删除'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulkDeleteDialog({
  count,
  preview,
  onClose,
  onDeleted,
}: {
  count: number;
  preview: readonly AuditEvent[];
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : '批量删除失败');
      setSubmitting(false);
    }
  };

  const shown = preview.slice(0, 5);

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="批量删除审计记录">
        <div className="panel__head">
          <h2 className="panel__title">批量删除审计记录</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将批量删除选中的 <strong>{count}</strong> 条审计记录。删除会打断哈希链，链校验将标记断点。
          </p>
          <div className="table-frame" style={{ border: 'none', padding: 0, marginBottom: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <tr key={e.id}>
                    <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.created_at)}</td>
                    <td>{actionLabel(e.action)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {count > shown.length && <p className="text-muted" style={{ fontSize: 12 }}>… 其余 {count - shown.length} 条省略</p>}
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting} onClick={handle}>
              {submitting ? '删除中…' : '确认批量删除'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditSkeleton({ rows }: { rows: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>
          <td><div className="skeleton-bar" style={{ width: '60%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '45%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '55%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '35%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '50%' }} /></td>
          <td><div className="skeleton-bar" style={{ width: '30%' }} /></td>
        </tr>
      ))}
    </tbody>
  );
}
